import React, {useState, useMemo, useCallback, useEffect, ReactNode} from 'react'
import {Accordion} from 'hec/components/ui/accordion'
import {AccordionContext} from './accordionContext'

type AccordionItem = {
    content: unknown[]
}

interface CustomAccordionProps {
    items: AccordionItem[]
    oneAtATime?: boolean
    arrowDirection?: 'right' | 'down'
    highlightExpandedItem?: boolean
    className?: string
    isEditing: (param?: unknown) => boolean
    renderItem: (params: {item: any; index: number}) => ReactNode
}

export const CustomAccordion: React.FC<CustomAccordionProps> = ({
    items,
    oneAtATime,
    renderItem,
    arrowDirection,
    highlightExpandedItem,
    isEditing,
    className,
    ...props
}) => {
    const flatContent = useMemo(() => items.flatMap((item) => item.content), [items])
    const allItemsForEditMode = useMemo(() => flatContent.map((_, i) => `item-${i}`), [flatContent])

    const [expandedItems, setExpandedItems] = useState(
        isEditing() ? allItemsForEditMode : oneAtATime ? '' : []
    )

    useEffect(() => {
        if (isEditing()) {
            setExpandedItems(allItemsForEditMode)
        }
    }, [allItemsForEditMode])

    const shouldExpandDetails = useCallback(
        (index: number) => {
            if (isEditing()) {
                return true
            } else if (oneAtATime) {
                return expandedItems === `item-${index}`
            } else {
                return expandedItems.includes(`item-${index}`)
            }
        },
        [expandedItems, oneAtATime]
    )

    return (
        <Accordion
            type={oneAtATime && !isEditing() ? 'single' : 'multiple'}
            collapsible
            value={expandedItems}
            onValueChange={setExpandedItems}
            className={className}
            {...props}
            data-testid="accordion-Accordion"
        >
            {flatContent.map((item, index) => (
                <AccordionContext.Provider
                    key={index}
                    value={{
                        shouldExpandDetails,
                        itemIndex: index,
                        arrowDirection,
                        highlightExpandedItem
                    }}
                >
                    {renderItem({item, index})}
                </AccordionContext.Provider>
            ))}
        </Accordion>
    )
}
